<script setup lang="ts">
  defineOptions({
    name: 'SignUpPage',
  })

  // Vue core imports
  import type { PropType } from 'vue'

  import { computed, onMounted, ref, watch } from 'vue'

  // External library imports
  import { useGlobal } from '@common/core-layer/composables/useGlobal'
  import { useI18n } from 'vue-i18n'

  import * as yup from 'yup'

  import { Lock01Icon, Mail01Icon, User01Icon } from '@gohighlevel/ghl-icons/24/outline'

  // Internal imports
  import CPButton from '@ui/components/CPButton.vue'

  import CPForm from '@ui/components/CPForm.vue'

  import CPFormItem from '@ui/components/CPFormItem.vue'

  import CPInput from '@ui/components/CPInput.vue'

  import CPText from '@ui/components/CPText.vue'

  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { useCaptchaChallenge } from '../../composables/useCaptchaChallenge'

  import { Screens } from '../../types/auth'

  import { getErrorMessage, SANITIZE_REGEX_PATTERN, validatePassword, validateText } from '../../utils'

  import Recaptcha from '../captcha/ReCaptcha.vue'
  import HoneypotField from '../captcha/HoneypotField.vue'

  import SignInWithGoogle from '../login/SignInWithGoogle.vue'

  import SignInWithGoogleKollab from '../login/SignInWithGoogleKollab.vue'

  // Props definitions
  const props = defineProps({
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
    email: {
      type: String,
      default: '',
    },
    isSsoDisabled: {
      type: Boolean,
      default: false,
    },
    deferSetPasswordSession: {
      type: Boolean,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: any): void
    (e: 'loginCallback', value: any): void
    (e: 'updateLoader', value: boolean): void
  }>()

  // Refs and reactive variables
  const usernameError = ref('')

  const emailError = ref('')

  const passwordError = ref('')

  const loading = ref(false)

  const signUpDisabled = ref(false)

  // Bot trap bound to <HoneypotField>; humans never see it, so any value means a bot.
  const honeypot = ref('')

  const { showRecaptcha, reCaptchaToken, onVerify, onError, onExpired, handleRateLimit } = useCaptchaChallenge(signUpDisabled)

  const errorMessage = ref()

  const otpToken = ref()

  const formValue = ref({
    email: props.email || '',
    password: '',
    username: '',
  })

  const formRef = ref()

  const { isGokollab } = useGlobal()

  const { t: $t } = useI18n()

  // Validation rules
  const rules = {
    email: {
      required: true,
      asyncValidator: async (_: any, value: string) => {
        emailError.value = ''

        if (!value) {
          emailError.value = $t('auth.validation.emailRequired')
          return Promise.reject(new Error($t('auth.validation.emailRequired')))
        }

        try {
          await yup.string().email($t('auth.validation.emailInvalid')).required($t('auth.validation.emailRequired')).validate(value)
          return Promise.resolve()
        } catch {
          emailError.value = $t('auth.validation.emailInvalid')
          return Promise.reject(new Error(emailError.value))
        }
      },
      trigger: ['input', 'blur'],
    },
    username: {
      required: true,
      validator: (_: any, value: string) => {
        usernameError.value = ''

        if (!value) {
          usernameError.value = $t('auth.validation.fullNameRequired')
          return false
        }

        try {
          // Keep this synchronous so form validation and existing unit tests behave consistently.
          // Use validateText (sync) instead of textValidation.validate (async Promise wrapper).
          const result = validateText(value, $t)
          if (result instanceof Error) throw result
          return true
        } catch {
          usernameError.value = $t('auth.validation.fullNameInvalid')
          return false
        }
      },
      message: $t('auth.validation.nameRequired'),
      trigger: ['input'],
    },
    password: {
      required: true,
      min: 8,
      validator: (rule: any, value: string) => {
        passwordError.value = ''
        const result = validatePassword(rule, value, $t)
        if (result !== true) {
          passwordError.value = result.message
          return false
        }
        return true
      },
      trigger: ['input', 'blur'],
    },
  }

  // Computed properties
  const isFormValid = computed(() => {
    return (
      formValue.value.email &&
      formValue.value.username &&
      formValue.value.password &&
      formValue.value.password.length >= 8 &&
      !emailError.value &&
      !usernameError.value &&
      !passwordError.value
    )
  })

  const handleValidateClick = async (formRef: any) => {
    // In-flight guard: Enter key + button click can both fire; each call sends an OTP email.
    if (loading.value) return false
    loading.value = true
    const isValidated = await validateForm(formRef)
    if (!isValidated || honeypot.value) {
      loading.value = false
      return false
    }
    // Defensive check to ensure values exist before trimming
    if (!formValue.value?.email || !formValue.value?.username || !formValue.value?.password) {
      console.error('Form values are undefined after validation')
      loading.value = false
      return
    }

    try {
      loading.value = true
      errorMessage.value = null
      const { data } = await useAuth().sendOtp({
        email: formValue.value.email.trim(),
        action: 'signup',
        captchaToken: reCaptchaToken.value,
        fullName: formValue.value.username.trim(),
      })

      emit('changeScreen', {
        page: Screens.VERIFY_SECURE_CODE,
        email: formValue.value.email,
        otpToken: data.otpToken,
        password: formValue.value.password.trim(),
        username: formValue.value.username.trim(),
        action: 'signup',
        referrerUser: props.referrerUser,
      })
      loading.value = false
    } catch (error: any) {
      if (!handleRateLimit(error)) {
        // Existing user on signup → send login OTP and switch to login verify screen
        const errorType = error?.response?.data?.type ?? error?.response?.data?.message?.type
        if (errorType === 'COMMON_USER_ALREADY_EXISTS') {
          try {
            const loginResponse = await useAuth().generateOTPForLogin(formValue.value.email.trim(), 'login', reCaptchaToken.value)
            emit('changeScreen', {
              page: Screens.VERIFY_SECURE_CODE,
              email: formValue.value.email,
              otpToken: loginResponse?.data?.otpToken,
              action: 'login',
            })
          } catch (loginError: any) {
            showRecaptcha.value = false
            errorMessage.value = getErrorMessage(loginError)
          }
        } else {
          errorMessage.value = getErrorMessage(error)
        }
      }
      loading.value = false
    }
  }

  const onFormValueChange = (value: any) => {
    errorMessage.value = null
    formValue.value = value
  }

  const updateLoader = (value: boolean) => {
    loading.value = value
  }

  // Lifecycle hooks
  onMounted(() => {
    loading.value = false
    if (props.email) {
      formValue.value.email = props.email
    }
  })

  // Watch effects
  watch(
    () => formValue?.value?.username,
    () => {
      usernameError.value = ''
    }
  )

  watch(
    () => formValue?.value?.email,
    () => {
      emailError.value = ''
    }
  )

  watch(
    () => formValue?.value?.password,
    value => {
      if (!value) {
        passwordError.value = $t('auth.validation.passwordFieldRequired')
        return
      }
      const result = validatePassword(null, value, $t)
      if (result === true) {
        passwordError.value = ''
      } else {
        passwordError.value = result.message
      }
    }
  )

  // User-defined functions
  async function validateForm(formRef: any): Promise<boolean> {
    try {
      await formRef.getForm().validate()
      return true
    } catch (e) {
      console.error('error', e)
      return false
    }
  }
</script>

<template>
  <div>
    <SignInWithGoogleKollab
      v-if="!isSsoDisabled && isGokollab"
      login-type="SIGN_UP"
      :referrer-user="props.referrerUser"
      @updateLoader="updateLoader"
      @changeScreen="(val: any) => emit('changeScreen', val)"
      @loginCallback="(val: any) => emit('loginCallback', val)"
    />
    <SignInWithGoogle
      v-else-if="!isSsoDisabled"
      login-type="SIGN_UP"
      :referrer-user="props.referrerUser"
      :defer-set-password-session="deferSetPasswordSession"
      @updateLoader="updateLoader"
      @changeScreen="(val: any) => emit('changeScreen', val)"
      @loginCallback="(val: any) => emit('loginCallback', val)"
    />
    <CPForm
      :id="createElementId('signup', 'modal', 'signup-form')"
      ref="formRef"
      class="flex flex-col items-center"
      :model="formValue"
      :rules="rules"
      @keyup.enter="handleValidateClick(formRef)"
    >
      <CPFormItem class="w-full" path="username" :label="$t('auth.fullName')" required>
        <CPInput
          :id="createElementId('signup', 'modal', 'username-input')"
          v-model="formValue.username"
          size="lg"
          :maxlength="50"
          :placeholder="$t('auth.fullNamePlaceholder')"
          @input="onFormValueChange(formValue)"
        >
          <template #prefix>
            <User01Icon class="mr-2 h-5 w-5 pr-1"></User01Icon>
          </template>
        </CPInput>
        <template v-if="usernameError" #feedback>
          <CPText size="sm" class="text-danger">
            {{ usernameError }}
          </CPText>
        </template>
      </CPFormItem>

      <CPFormItem class="w-full" path="email" :label="$t('auth.email')" required>
        <CPInput
          :id="createElementId('signup', 'modal', 'email-input')"
          v-model="formValue.email"
          size="lg"
          :placeholder="$t('auth.emailPlaceholder')"
          :disabled="!!props.email"
          @input="onFormValueChange(formValue)"
        >
          <template #prefix>
            <Mail01Icon class="mr-2 h-5 w-5 pr-1"></Mail01Icon>
          </template>
        </CPInput>
        <template #feedback>
          <CPText size="sm" :class="emailError ? 'text-danger' : 'text-clientportal-font-secondary'">
            {{ emailError || $t('auth.messages.accountConfirmationCode') }}
          </CPText>
        </template>
      </CPFormItem>

      <CPFormItem class="w-full" path="password" :label="$t('auth.password')" required>
        <CPInput
          :id="createElementId('signup', 'modal', 'password-input')"
          v-model="formValue.password"
          size="lg"
          type="password"
          :placeholder="$t('auth.passwordPlaceholder')"
          show-password-on="click"
          @input="onFormValueChange(formValue)"
        >
          <template #prefix>
            <Lock01Icon class="mr-2 h-5 w-5 pr-1"></Lock01Icon>
          </template>
        </CPInput>
        <template #feedback>
          <CPText
            :id="createElementId('signup', 'modal', 'password-field-feedback')"
            size="sm"
            :class="passwordError ? 'text-danger' : 'text-clientportal-font-secondary'"
          >
            {{ passwordError || $t('auth.messages.createStrongPassword') }}
          </CPText>
        </template>
      </CPFormItem>

      <div v-if="errorMessage" class="text-danger w-full">
        <CPText size="sm">{{ errorMessage }}</CPText>
      </div>

      <Recaptcha
        v-if="showRecaptcha"
        :id="createElementId('signup', 'modal', 'recaptcha')"
        class="flex w-full justify-center"
        @expired="onExpired"
        @verify="onVerify"
        @error="onError"
      />

      <CPButton
        :id="createElementId('signup', 'modal', 'signup-button')"
        class="mt-4 w-full rounded-xl shadow-sm"
        type="primary"
        size="lg"
        :loading="loading"
        :disabled="loading || signUpDisabled || !isFormValid"
        @click="handleValidateClick(formRef)"
      >
        {{ $t('auth.signupAndVerifyEmail') }}
      </CPButton>

      <div class="text-secondary mt-4 flex w-full justify-center gap-1">
        <CPText size="md" weight="regular" html-tag="span">
          {{ $t('auth.alreadyHaveAccount') }}
        </CPText>

        <CPButton
          :id="createElementId('signup', 'modal', 'login-link')"
          type="text"
          color="secondary"
          size="xs"
          class="cursor-pointer hover:underline"
          @click="
            emit('changeScreen', {
              page: Screens.LOGIN,
              email: formValue.email,
            })
          "
        >
          <CPText size="md" weight="semibold" html-tag="span">
            {{ $t('auth.logIn') }}
          </CPText>
        </CPButton>
      </div>
      <HoneypotField v-model="honeypot" />
    </CPForm>
  </div>
</template>

<style scoped>
  .hr-form {
    gap: 16px;
  }

  .hr-divider:not(.hr-divider--vertical) {
    margin: 0px;
  }

  :deep(.hr-input--disabled) {
    background-color: #f5f5f5 !important;
    cursor: not-allowed !important;
  }

  :deep(.hr-input--disabled input) {
    color: #666 !important;
    cursor: not-allowed !important;
  }
</style>
